/**
 * NLP Service
 * Translates natural language queries to GIS query parameters using OpenAI
 * Uses backend proxy to keep API keys secure
 */

import { featureServices, isWaterTenant, isEmissionsTenant } from '../config/gisConfig';
import conversationService from './conversationService';

/**
 * Emissions system prompt — Picarro POC (Verdant Falls gas utility + Crestwood Refinery fenceline)
 */
const EMISSIONS_SYSTEM_PROMPT = `You are a GIS query assistant for a gas utility leak-survey + fenceline emissions monitoring platform (Picarro-style). Convert natural language queries into structured GIS query parameters.

COMPANY CONTEXT:
A natural gas utility ("Verdant Gas Utility") operates a 200 sq-mile distribution network across 4 districts: Jefferson, Northside, Eastgate, Riverside. Picarro CRDS mobile vehicles drive routes producing methane readings, leak indications (Grade 1 = non-hazardous, Grade 2 = probable, Grade 3 = hazardous emergency). Leak risk is scored 1–100. A separate fenceline monitoring deployment ("Crestwood Refinery") has 14 perimeter sensors detecting anomalies and attributing them to internal process units.

AVAILABLE LAYERS (case-sensitive):

1. "leaks" — Leak Indications (CRDS):
   - id, route_id, survey_period (Q3-2026, Q2-2026, Q3-2025), survey_date, district, district_name
   - peak_concentration_ppm (number), grade (1/2/3), risk_score (1–100), confidence
   - scenario_code (PIPE_CORROSION, JOINT_LEAK, SERVICE_TEE, METER_LEAK, VINTAGE_FAILURE, SERVICE_RISER)
   - status (Open/Pending/Closed), recurring_anchor

2. "survey_routes" — CRDS mobile survey routes (LineString):
   - id, period, district, vehicle_id, operator, total_mileage, completion_status

3. "distribution_mains" — gas distribution pipes (LineString):
   - id, material (cast iron / bare steel / polyethylene), install_year, district, pressure (low/medium/high), diameter_in

4. "transmission_mains" — high-pressure backbone

5. "service_lines" — final connection to premises

6. "pipe_risk" — pipe risk scoring layer (LineString):
   - composite_risk_score (0–100), risk_tier (Critical/High/Medium/Low), leak_history_count, replacement_priority (1=highest)

7. "work_orders_open" — active repair assignments (Point):
   - priority (Emergency/Urgent/Routine), assigned_crew, target_date, grade, risk_score

8. "work_orders_closed" — completed repairs (Point)

9. "sensitive_premises" — schools, hospitals, daycare, senior living (Point):
   - address_type, sensitive (true/false), district, name

10. "districts" — district boundaries (Polygon)

11. "sensors" — Crestwood Refinery fenceline sensors (Point):
    - sensor_id, array_id, sensor_type (CRDS), communication_status

12. "facility" — refinery boundary, "process_units" — internal units (Tank Farm, FCC, Crude Distillation, Flare Stack)

13. "dispersion_plumes" — modelled emission plumes overlaid on map for source attribution

RESPONSE FORMAT (JSON):
{
  "layer": "leaks|survey_routes|distribution_mains|pipe_risk|work_orders_open|sensitive_premises|districts|sensors|facility|process_units|dispersion_plumes",
  "conditions": [{"field": "district", "operator": "=", "value": "JEFFERSON", "type": "string"}],
  "zoom": true,
  "description": "Showing Grade 2+ leaks in Jefferson District",
  "confidence": 0.9
}

PROXIMITY QUERIES (leaks within N feet of sensitive premises):
For queries like "Grade 2 leaks within 100 feet of a school or hospital" return:
{
  "layer": "leaks",
  "conditions": [{"field": "grade", "operator": "=", "value": 2, "type": "number"}],
  "proximity_to": "sensitive_premises",
  "within_ft": 100,
  "premise_type": "school|hospital|both",
  "zoom": true,
  "description": "Grade 2 leaks within 100 ft of a school or hospital",
  "confidence": 0.9
}

ANALYTICS QUERIES (summarize, breakdown, full analytics, by district, by grade):
{ "layer": "analytics", "groupBy": "district|grade|operator|status", "description": "...", "confidence": 0.9 }

SCADA / OPERATIONS QUERIES (HIGHEST PRIORITY):
For queries about leak alerts, emergency response, crew dispatch, anomaly attribution, fenceline events, return:
{
  "layer": "scada",
  "scope": "region|asset",
  "filters": { "county": "Jefferson", "severity": "critical", "asset_id": "LK-00012" },
  "intent": "list_alerts|asset_dossier|nearest_crew|impact",
  "description": "Showing Grade 3 leaks in Jefferson District",
  "confidence": 0.9
}

SCADA TRIGGER WORDS: alert, alarm, leak indication, grade 3, emergency, what's wrong, status of, sensor, anomaly, fenceline, methane spike, source attribution, plume, tank farm, FCC, nearest crew (for ASSET dispatch only).

CREW LAYER TRIGGER WORDS (priority over SCADA): dispatch plan, dispatch list, crew availability, available crews, crew schedule, crew roster, tomorrow's crews, plan for tomorrow. These are PLANNING queries — route to layer="crews", NOT scada.

If the query mentions "dispatch" OR "crew" but does NOT name a specific asset (LK-NNNN / WO-NNNN / SENSOR-XN), classify as layer="crews".
If it names a specific asset, classify as layer="scada" intent="nearest_crew".

DISAMBIGUATION:
- "show Grade 2+ leaks in Jefferson Q3" → layer: leaks, conditions on grade and district, period
- "build a dispatch plan for tomorrow" / "show crew availability" / "create dispatch plan" / "which crews are available" → layer: crews (NOT scada), conditions on availability/district as needed
- "who is the nearest crew to fix LK-XXXX" → layer: scada, intent: nearest_crew (this is an asset-specific dispatch lookup, different from planning)
- "which pipe segments should we replace" → layer: pipe_risk, sorted by composite_risk_score
- "show pipe replacement priority BY DISTRICT" → layer: pipe_risk, groupBy: "district"
- "sensor B3 anomaly source" → layer: scada, intent: asset_dossier, filters.asset_id: SENSOR-B3
- "compliance summary Jefferson Q3" → layer: analytics, groupBy: status

CREW DISPATCH PLANNING is its own layer (crews). Don't route planning questions through scada.

Important: District names in UPPERCASE (JEFFERSON, NORTHSIDE, EASTGATE, RIVERSIDE). Always return valid JSON.`;

/**
 * Water system prompt — Select Water Solutions
 */
const WATER_SYSTEM_PROMPT = `You are a GIS query assistant for Select Water Solutions. Convert natural language queries into structured GIS query parameters for water asset management.

COMPANY CONTEXT:
Select Water Solutions manages water sourcing, disposal, treatment, and transfer infrastructure across the Permian Basin.

AVAILABLE LAYERS AND FIELD NAMES (case-sensitive):

1. "sources" - Water Sources:
   - id (string), name (string), county (string), basin (string)
   - type (string) - source type
   - daily_volume_bbl (number) - daily volume in barrels
   - operator (string), status (string), quality_tds (number)

2. "disposal" - Disposal Wells:
   - id (string), name (string), county (string), basin (string)
   - type (string), capacity_bbl (number), daily_volume_bbl (number)
   - utilization_pct (number), operator (string), status (string)
   - formation (string), permit (string)

3. "pipelines" - Transfer Pipelines:
   - id (string), name (string), county (string), type (string)
   - length_miles (number), capacity_bbl_day (number), utilization_pct (number)
   - operator (string), status (string), product (string)

4. "treatment" - Treatment Facilities:
   - id (string), name (string), county (string), basin (string)
   - type (string), capacity_bbl (number), daily_volume_bbl (number)
   - recycling_rate_pct (number), operator (string), status (string)
   - technology (string), output_quality (string)

RESPONSE FORMAT (JSON):
{
  "layer": "sources|disposal|pipelines|treatment",
  "conditions": [{"field": "county", "operator": "=", "value": "MIDLAND", "type": "string"}],
  "zoom": false,
  "description": "Showing disposal wells in Midland",
  "confidence": 0.9
}

ANALYTICS QUERIES:
For summarize/analyze/breakdown queries, return:
{
  "layer": "analytics",
  "groupBy": "operator|county|status|type",
  "description": "Summarizing results by operator and county",
  "confidence": 0.9
}

Trigger analytics when user says: summarize, analyze, breakdown, group by, count by, statistics, full analytics, show totals, by operator, by county.

SCADA / OPERATIONS QUERIES (HIGHEST PRIORITY):
**Before considering any other layer or analytics classification, check if the query mentions SCADA / operations keywords. If yes, return layer: scada — DO NOT classify it as analytics, sources, disposal, pipelines, or treatment.**

For queries about alerts, telemetry, operational status, troubleshooting, or crew dispatch, return:
{
  "layer": "scada",
  "scope": "region|asset",
  "filters": { "county": "Reeves", "severity": "critical", "asset_id": "DW-0042" },
  "intent": "list_alerts|asset_dossier|nearest_crew|impact",
  "description": "Showing critical disposal alerts in Reeves County",
  "confidence": 0.9
}

SCADA TRIGGER WORDS (any of these → layer: scada):
alert, alerts, alarm, alarms, telemetry, SCADA, status of, what's wrong with, problem with, offline, down, downtime, outage, pump offline, high pressure, low pressure, pressure drop, pressure spike, over capacity, overcapacity, troubleshoot, troubleshooting, fix, dispatch, crew, who can fix, who's nearest, nearest crew, who is closest, ETA, operational impact, capacity offline, recovery rate, leak, recycling problem, fault, faulty, failure, broken, motor fault, valve fault, filter clog, comm loss, comm degraded.

DISAMBIGUATION RULES:
- "show all critical alerts" → layer: scada (NOT analytics, even though it has "all")
- "alerts in Reeves County" → layer: scada (NOT sources/disposal, even though no asset type is named)
- "who's nearest to fix DW-42" → layer: scada, intent: nearest_crew
- "summarize alerts by severity" → layer: scada, intent: list_alerts (NOT analytics)
- "summarize disposal wells by operator" → layer: analytics (no SCADA keyword present)

Set "scope": "asset" if the query references a specific asset id (e.g. DW-0042, P-23, TF-0275, WS-0100) or asks "what's wrong with X". Set "scope": "region" if the query is about a county/basin/severity scope or all counties.

Set "intent" to:
  - "asset_dossier" — full SCADA picture for one asset (alerts + telemetry + crew + impact)
  - "list_alerts" — alerts in a region or filtered set ("show alerts", "list alerts", "all critical alerts")
  - "nearest_crew" — who can respond / dispatch question
  - "impact" — operational impact / capacity-offline question

Important: Always return valid JSON. Match county names in uppercase. SCADA queries take priority over both layer queries AND analytics queries when SCADA keywords are present.`;

/**
 * System prompt for the LLM — TPL
 * This defines the GIS query translation task
 */
const SYSTEM_PROMPT = `You are a GIS query assistant for Texas Pacific Land Corporation (TPL). Convert natural language queries into structured GIS query parameters for TPL's land and royalty management system.

COMPANY CONTEXT:
TPL owns 880,000+ surface acres and 207,000 net royalty acres in West Texas Permian Basin across 20+ counties. TPL is NOT an oil/gas producer - they are LANDOWNERS who earn royalties from operators. Key business: land tract management, water services, pipeline easements, and surface lease agreements.

AVAILABLE LAYERS AND FIELD NAMES (case-sensitive):

1. "tracts" - TPL Land Tracts (TP_Assets_All):
   - TractID (string) - Unique tract identifier
   - TractType (string) - F=Fee, E=Easement, L=Lease, S=Surface, R=Royalty
   - CountyName (string) - County name
   - Block (string) - Block number
   - Sec (string) - Section number
   - TractAcres (number) - Acreage
   - Status (string) - Active, Expired, Pending
   - ORRI (number) - Override royalty interest (decimal: 0.025 = 2.5%)
   - NRI (number) - Net revenue interest (decimal: 0.02 = 2%)
   - NMA (number) - Net mineral acres
   - NRA (number) - Net royalty acres
   - DealName (string) - Deal/acquisition name
   - Grantee (string) - Grantee name
   - ContractID (string) - Contract identifier

2. "wells" - TPL Royalty Wells (TP_Royalty_Wells):
   - API (string) - API number (10-digit)
   - API14 (string) - Full 14-digit API
   - WellName (string) - Well name
   - WellPadID (string) - Well pad identifier
   - ENVOperator (string) - Operator name (e.g., "Diamondback Energy", "EOG Resources")
   - ENVWellStatus (string) - Well status: PRODUCING, DUC, DRILLING, PERMITTED, SHUT-IN, P&A
   - ENVInterval (string) - Formation: WOLFCAMP A/B/D, BONE SPRING, AVALON, DELAWARE, SPRABERRY
   - County (string) - County name
   - LateralLength (number) - Lateral length in feet
   - ActiveNRI (number) - Active net revenue interest
   - AllocatedNRI (number) - Allocated NRI
   - DSUName (string) - Development spacing unit name
   - DealName (string) - Deal name
   - Trajectory (string) - Well trajectory (HORIZONTAL, VERTICAL)
   - Vintage (string) - Year vintage

3. "blocks" - Landgrid Blocks:
   - SURVEYBLO3 (string) - Block number
   - CountyName (string) - County name
   - SURVEYNAME (string) - Survey name

4. "sections" - Survey Sections:
   - BlkSec (string) - Block-Section identifier
   - County (string) - County name
   - SURVEYSEC4 (string) - Section number

5. "contracts" - TPL Contracts (Easements/Leases):
   - TractID (string) - Tract identifier
   - TractType (string) - Contract type
   - ContractType (string) - Pipeline, Water, Surface, etc.
   - ContractCat (string) - Contract category
   - Status (string) - Active, Expired
   - Grantee (string) - Grantee/operator
   - Rate (number) - Rate value
   - EffectiveDate (string) - Effective date
   - Term (string) - Contract term

6. "dsus" - Development Spacing Units

7. "activeRigs" - Active Drilling Rigs

COMMON OPERATORS (use LIKE for partial matching):
Diamondback, Exxon, ExxonMobil, Occidental, Oxy, Ovintiv, APA Corp, Apache, EOG, Mewbourne, Permian Resources, ConocoPhillips, Devon

COUNTIES (SE New Mexico / West Texas):
Texas: REEVES, CULBERSON, LOVING, MARTIN, MIDLAND, ECTOR, WARD, WINKLER, ANDREWS, PECOS, CRANE, HOWARD, GLASSCOCK, REAGAN
New Mexico: LEA, EDDY, CHAVES

FORMATIONS/INTERVALS:
WOLFCAMP, WOLFCAMP A, WOLFCAMP B, WOLFCAMP D, BONE SPRING, AVALON, DELAWARE, SPRABERRY

WELL STATUS VALUES:
PRODUCING, DUC (Drilled Uncompleted), DRILLING, PERMITTED, SHUT-IN, P&A (Plugged & Abandoned)

SQL Operators:
- Use "=" for exact matches
- Use "LIKE" for partial matches (with % wildcards)
- Use "IN" for multiple values
- Use ">", "<", ">=", "<=" for numeric/date comparisons
- Use "BETWEEN" for ranges

Date Format:
- Use ISO format: YYYY-MM-DD
- For date queries: "PermitApprovedDate >= DATE '2025-08-01'"
- For date ranges: "PermitApprovedDate BETWEEN DATE '2025-08-01' AND DATE '2025-08-31'"

Return JSON with this exact structure:
{
  "layer": "tracts" | "contracts" | "wells",
  "conditions": [
    {
      "field": "FieldName",
      "operator": "=" | ">" | "<" | ">=" | "<=" | "LIKE" | "IN" | "BETWEEN",
      "value": "value or array of values",
      "type": "string" | "number" | "date"
    }
  ],
  "zoom": true | false,
  "description": "Human-readable explanation of what this query does",
  "confidence": 0.0 to 1.0 (how confident you are in this translation)
}

Examples:

User: "Show me tract F-1234"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "TractID", "operator": "LIKE", "value": "%F-1234%", "type": "string"}],
  "zoom": true,
  "description": "Displaying tract F-1234 details",
  "confidence": 0.95
}

User: "Find all tracts in Howard County"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "CountyName", "operator": "=", "value": "HOWARD", "type": "string"}],
  "zoom": true,
  "description": "Showing all TPL tracts in Howard County",
  "confidence": 0.95
}

User: "Show tracts with acreage over 500"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "TractAcres", "operator": ">", "value": 500, "type": "number"}],
  "zoom": false,
  "description": "Filtering tracts with acreage over 500",
  "confidence": 0.9
}

User: "Find tracts with NRI over 0.01"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "NRI", "operator": ">", "value": 0.01, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with NRI over 0.01 (1%)",
  "confidence": 0.9
}

User: "Show tracts with NRA over 20"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "NRA", "operator": ">", "value": 20, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with net royalty acres over 20",
  "confidence": 0.9
}

User: "Find tracts with ORRI over 2%"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "ORRI", "operator": ">", "value": 0.02, "type": "number"}],
  "zoom": false,
  "description": "Showing tracts with override royalty interest over 2% (0.02 decimal)",
  "confidence": 0.9
}

User: "Show fee tracts in Reeves County"
Response:
{
  "layer": "tracts",
  "conditions": [
    {"field": "TractType", "operator": "=", "value": "F", "type": "string"},
    {"field": "CountyName", "operator": "=", "value": "Reeves", "type": "string"}
  ],
  "zoom": true,
  "description": "Showing fee tracts (TractType=F) in Reeves County",
  "confidence": 0.95
}

User: "Show wells operated by Diamondback"
Response:
{
  "layer": "wells",
  "conditions": [{"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}],
  "zoom": false,
  "description": "Displaying wells operated by Diamondback",
  "confidence": 0.9
}

User: "Show producing wells in Reeves County"
Response:
{
  "layer": "wells",
  "conditions": [
    {"field": "County", "operator": "=", "value": "REEVES", "type": "string"},
    {"field": "ENVWellStatus", "operator": "=", "value": "PRODUCING", "type": "string"}
  ],
  "zoom": true,
  "description": "Displaying producing wells in Reeves County",
  "confidence": 0.95
}

User: "Find Wolfcamp wells with lateral over 10000 ft"
Response:
{
  "layer": "wells",
  "conditions": [
    {"field": "ENVInterval", "operator": "LIKE", "value": "%WOLFCAMP%", "type": "string"},
    {"field": "LateralLength", "operator": ">", "value": 10000, "type": "number"}
  ],
  "zoom": false,
  "description": "Showing Wolfcamp wells with lateral length over 10,000 feet",
  "confidence": 0.9
}

User: "Show tracts in Block 32"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Block", "operator": "LIKE", "value": "%32%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts in Block 32",
  "confidence": 0.9
}

User: "Find T&P RR CO survey tracts"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Survey", "operator": "=", "value": "T&P RR CO", "type": "string"}],
  "zoom": false,
  "description": "Showing tracts in T&P RR CO survey",
  "confidence": 0.9
}

User: "Show tracts in Section 10"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "Sec", "operator": "=", "value": "10", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts in Section 10",
  "confidence": 0.9
}

User: "Show SM ORRI Deal 1 tracts"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "DealName", "operator": "=", "value": "SM ORRI Deal 1", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts from SM ORRI Deal 1",
  "confidence": 0.95
}

User: "Find Tumbleweed Deal assets"
Response:
{
  "layer": "tracts",
  "conditions": [{"field": "DealName", "operator": "LIKE", "value": "%Tumbleweed%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts from Tumbleweed Deal",
  "confidence": 0.9
}

User: "Show me just the Diamondback ones"
Response: (This is a FOLLOW-UP query - use previous context)
{
  "layer": "wells",
  "conditions": [
    {"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}
  ],
  "zoom": false,
  "description": "Filtering previous results to Diamondback only",
  "confidence": 0.85,
  "isFollowUp": true
}

User: "What's the total acreage?"
Response: (ANALYTICS query - return special format)
{
  "layer": "analytics",
  "analysisType": "aggregate",
  "function": "sum",
  "field": "TractAcres",
  "description": "Calculating total acreage from previous results",
  "confidence": 0.95
}

User: "How many tracts do we have in Reeves County?"
Response: (ANALYTICS query)
{
  "layer": "analytics",
  "analysisType": "count",
  "baseQuery": {
    "layer": "tracts",
    "conditions": [{"field": "CountyName", "operator": "=", "value": "REEVES", "type": "string"}]
  },
  "description": "Counting tracts in Reeves County",
  "confidence": 0.95
}

User: "Summarize the wells by operator"
Response: (ANALYTICS query)
{
  "layer": "analytics",
  "analysisType": "groupBy",
  "field": "ENVOperator",
  "description": "Grouping wells by operator with counts",
  "confidence": 0.95
}

User: "Show all tracts within 5 miles of tract F-1234"
Response: (PROXIMITY query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-1234",
  "radius": 5,
  "unit": "miles",
  "description": "Finding all tracts within 5 miles of tract F-1234",
  "confidence": 0.95
}

User: "Find wells near DSU ABC-123 within 2 miles"
Response: (PROXIMITY query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerDSU": "ABC-123",
  "radius": 2,
  "unit": "miles",
  "description": "Finding wells within 2 miles of DSU ABC-123",
  "confidence": 0.9
}

User: "What tracts are close to well 42-109-32930?"
Response: (PROXIMITY query - default to 5 miles if not specified)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerWell": "42-109-32930",
  "radius": 5,
  "unit": "miles",
  "description": "Finding tracts near well 42-109-32930 (within 5 miles)",
  "confidence": 0.85
}

User: "Show tracts within 5 miles of F-1234 with ORRI over 2%"
Response: (COMBINED PROXIMITY + ATTRIBUTE query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-1234",
  "radius": 5,
  "unit": "miles",
  "conditions": [
    {"field": "ORRI", "operator": ">", "value": 2, "type": "number"}
  ],
  "description": "Finding tracts within 5 miles of F-1234 with ORRI over 2%",
  "confidence": 0.9
}

User: "Find wells near tract F-5678 with status PRODUCING"
Response: (COMBINED PROXIMITY + ATTRIBUTE query)
{
  "layer": "proximity",
  "analysisType": "buffer",
  "centerTract": "F-5678",
  "radius": 5,
  "unit": "miles",
  "conditions": [
    {"field": "ENVWellStatus", "operator": "=", "value": "PRODUCING", "type": "string"}
  ],
  "description": "Finding producing wells near tract F-5678",
  "confidence": 0.9
}

User: "Show pipeline easements"
Response:
{
  "layer": "contracts",
  "conditions": [{"field": "ContractType", "operator": "LIKE", "value": "%Pipeline%", "type": "string"}],
  "zoom": false,
  "description": "Showing pipeline easement contracts",
  "confidence": 0.95
}

User: "Find active contracts with rate over 1000"
Response:
{
  "layer": "contracts",
  "conditions": [
    {"field": "Status", "operator": "=", "value": "Active", "type": "string"},
    {"field": "Rate", "operator": ">", "value": 1000, "type": "number"}
  ],
  "zoom": false,
  "description": "Finding active contracts with rate over $1,000",
  "confidence": 0.9
}

User: "Show surface leases granted to Exxon"
Response:
{
  "layer": "contracts",
  "conditions": [
    {"field": "ContractType", "operator": "LIKE", "value": "%Surface%", "type": "string"},
    {"field": "Grantee", "operator": "LIKE", "value": "%Exxon%", "type": "string"}
  ],
  "zoom": false,
  "description": "Showing surface lease contracts granted to Exxon",
  "confidence": 0.9
}

User: "Find water rights contracts"
Response:
{
  "layer": "contracts",
  "conditions": [{"field": "ContractType", "operator": "LIKE", "value": "%Water%", "type": "string"}],
  "zoom": false,
  "description": "Showing water rights contracts",
  "confidence": 0.95
}

Important:
- Always return valid JSON
- Use exact field names (case-sensitive)
- For OR conditions, use IN operator with array of values
- For follow-up queries (containing "just", "only", "those", "these"), set isFollowUp=true
- For analytics queries (containing "how many", "total", "average", "summarize"), use layer="analytics"
- For proximity/spatial queries (containing "within", "near", "close to", "around"), use layer="proximity" with analysisType="buffer"
- For contract-based queries (containing "monthly revenue", "contracts", "expiring", "pipeline easement", "water rights"), use layer="contracts" with contractFilters object
- For proximity queries, extract centerTract, radius (default 5 if not specified), and unit (miles or kilometers)
- For single ID lookups or specific location queries, set zoom=true
- For general filters, set zoom=false
- Set confidence lower if the query is ambiguous
- Match operator names flexibly (e.g., "bpx" matches "bpx energy")

MULTI-LAYER QUERIES (WOW FEATURE):
When user asks to see multiple data types together (tracts AND wells, or "show everything", "show all data", "show all layers"), return a multi-layer query:

User: "Show all data in Reeves County"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "County", "operator": "=", "value": "REEVES", "type": "string"}],
  "zoom": true,
  "description": "Showing all tracts and wells in Reeves County",
  "confidence": 0.95
}

User: "Show tracts and wells operated by Diamondback"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "ENVOperator", "operator": "LIKE", "value": "%Diamondback%", "type": "string"}],
  "zoom": true,
  "description": "Showing tracts and wells operated by Diamondback",
  "confidence": 0.9
}

User: "Show everything in Howard County"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells", "contracts"],
  "conditions": [{"field": "County", "operator": "=", "value": "HOWARD", "type": "string"}],
  "zoom": true,
  "description": "Showing all data (tracts, wells, contracts) in Howard County",
  "confidence": 0.95
}

User: "Display all layers for Culberson"
Response:
{
  "layer": "multi",
  "layers": ["tracts", "wells"],
  "conditions": [{"field": "CountyName", "operator": "=", "value": "CULBERSON", "type": "string"}],
  "zoom": true,
  "description": "Displaying all layers for Culberson County",
  "confidence": 0.95
}

Note: For multi-layer queries, use County field for wells and CountyName for tracts. The backend will handle field mapping.`;

/**
 * Translate natural language query to GIS parameters
 * Uses backend proxy to keep OpenAI API key secure
 */
export async function translateQuery(userQuery, previousResults = null) {
  try {
    // Add user message to conversation history
    conversationService.addMessage('user', userQuery);

    // ── PRECLASSIFIER (emissions): a few high-confidence patterns bypass the LLM
    // because the LLM has been observed mis-routing them to scada. Demo-critical
    // routes get pinned here.
    if (isEmissionsTenant) {
      const lq = userQuery.toLowerCase();
      const hasAssetRef = /\b(LK|WO|DM|TM|SL|PR|SENSOR)-?\s?\d/i.test(userQuery);
      const isCrewPlan = /(crew availability|crew schedule|crew roster|dispatch plan|dispatch list|available crews|crews available|tomorrow'?s crews|plan.*tomorrow|create.*dispatch|build.*dispatch)/i.test(lq);
      if (isCrewPlan && !hasAssetRef) {
        const districts = { jefferson: 'JEFFERSON', northside: 'NORTHSIDE', eastgate: 'EASTGATE', riverside: 'RIVERSIDE' };
        const dKey = Object.keys(districts).find(d => lq.includes(d));
        const conditions = dKey ? [{ field: 'district', operator: '=', value: districts[dKey], type: 'string' }] : [];
        console.log('[preclassifier] crew-planning intent — bypassing LLM, routing to layer:crews');
        return {
          layer: 'crews', conditions, zoom: true,
          description: `Crew availability${dKey ? ` in ${dKey}` : ''} for dispatch planning`,
          confidence: 0.95
        };
      }
      // Proximity preclassifier (school/hospital + Nft + grade) — LLM occasionally returns sensitive_premises only.
      if (/(school|hospital|sensitive)/i.test(lq) && (/within\s+\d+/i.test(lq) || /\bgrade\s*[123]\b/i.test(lq))) {
        const ftMatch = lq.match(/within\s+(\d+)\s*(ft|feet)/i);
        const gradeMatch = lq.match(/grade\s+([123])/i);
        const within_ft = ftMatch ? parseInt(ftMatch[1], 10) : 100;
        const conditions = [];
        if (gradeMatch) conditions.push({ field: 'grade', operator: '=', value: parseInt(gradeMatch[1], 10), type: 'number' });
        const premise_type = lq.includes('school') && !lq.includes('hospital') ? 'school'
          : lq.includes('hospital') && !lq.includes('school') ? 'hospital' : 'both';
        const premiseLabel = premise_type === 'both' ? 'school or hospital' : premise_type;
        console.log('[preclassifier] proximity intent — bypassing LLM');
        return {
          layer: 'leaks', conditions, proximity_to: 'sensitive_premises', within_ft, premise_type,
          zoom: true,
          description: `${gradeMatch ? `Grade ${gradeMatch[1]} ` : ''}leaks within ${within_ft} ft of a ${premiseLabel}`,
          confidence: 0.95
        };
      }
      // "By district" grouping for pipe risk
      if (/pipe.*priority.*by district|pipe replacement.*by district|priority by district/i.test(lq)) {
        console.log('[preclassifier] pipe_risk by district — bypassing LLM');
        return {
          layer: 'pipe_risk', conditions: [], groupBy: 'district', zoom: true,
          description: 'Pipe replacement priority grouped by district',
          confidence: 0.95
        };
      }
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

    // Build messages with conversation context
    const activePrompt = isEmissionsTenant
      ? EMISSIONS_SYSTEM_PROMPT
      : isWaterTenant
      ? WATER_SYSTEM_PROMPT
      : SYSTEM_PROMPT;
    const messages = [
      { role: 'system', content: activePrompt }
    ];

    // Add conversation history for context (last 3 exchanges)
    const context = conversationService.getContext();
    if (context.history.length > 0) {
      const recentHistory = context.history.slice(-6); // Last 3 exchanges (user + assistant)
      recentHistory.forEach(msg => {
        messages.push({
          role: msg.role,
          content: msg.content
        });
      });
    }

    // Add context about last query results if available
    if (context.lastResults) {
      messages.push({
        role: 'system',
        content: `PREVIOUS QUERY CONTEXT: The user's last query returned ${context.lastResults.count} ${context.lastResults.layer} records. Summary: ${JSON.stringify(context.lastResults.summary)}`
      });
    }

    // Add current user query
    messages.push({
      role: 'user',
      content: userQuery
    });

    const response = await fetch(`${backendUrl}/api/openai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: messages,
        response_format: { type: 'json_object' },
        temperature: 0.3,
        max_tokens: 500
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'OpenAI request failed');
    }

    const completion = await response.json();
    const intent = JSON.parse(completion.choices[0].message.content);

    // Handle analytics queries
    if (intent.layer === 'analytics') {
      return intent;
    }

    // Handle multi-layer queries (WOW FEATURE)
    if (intent.layer === 'multi') {
      // Validate multi-layer query structure
      if (!intent.layers || !Array.isArray(intent.layers) || intent.layers.length === 0) {
        throw new Error('Invalid multi-layer query - missing layers array');
      }
      console.log('🌟 Multi-layer query detected:', intent.layers);
      return intent;
    }

    // Handle proximity queries
    if (intent.layer === 'proximity') {
      // Validate proximity query structure - accept both single and multi-center
      const hasCenterTract = intent.centerTract || (intent.centerTracts && intent.centerTracts.length > 0);
      if (!hasCenterTract || !intent.radius) {
        throw new Error('Invalid proximity query - missing centerTract/centerTracts or radius');
      }
      return intent;
    }

    // Handle contract-based queries
    if (intent.layer === 'contracts') {
      // Validate contract query structure
      if (!intent.contractFilters) {
        throw new Error('Invalid contract query - missing contractFilters');
      }
      return intent;
    }

    // SCADA queries don't carry a `conditions` array — they use { scope, filters, intent }.
    if (intent.layer === 'scada') {
      return intent;
    }
    // Analytics queries don't carry `conditions` either — they use { groupBy }.
    if (intent.layer === 'analytics') {
      return intent;
    }
    // Symbology / styling commands may not have conditions either
    if (intent.layer === 'symbology' || intent.layer === 'style') {
      return intent;
    }

    // Validate the response for spatial queries
    if (!intent.layer || !intent.conditions) {
      throw new Error('Invalid response from LLM');
    }

    // Normalize conditions for SQL generation
    intent.conditions = intent.conditions.map(cond => {
      // Handle IN operator - convert to array if not already
      if (cond.operator === 'IN' && !Array.isArray(cond.value)) {
        cond.value = [cond.value];
      }
      return cond;
    });

    // Add assistant response to conversation history
    conversationService.addMessage('assistant', intent.description);

    console.log('Query translated:', intent);

    return intent;
  } catch (error) {
    console.error('NLP translation failed:', error);

    // Try fallback pattern matching
    return fallbackPatternMatching(userQuery);
  }
}

/**
 * Fallback pattern matching if OpenAI fails
 * Uses simple regex patterns to extract query intent
 */
function fallbackPatternMatching(query) {
  const lowerQuery = query.toLowerCase();

  // ─── Emissions tenant (Picarro POC) fallback patterns ───────────────────
  if (isEmissionsTenant) {
    // CREW PLANNING — route to crews layer, not scada. Matches "dispatch plan", "crew availability",
    // "available crews tomorrow", "which crews can do X". Distinct from "nearest crew to fix Y" (asset-specific dispatch).
    const isPlanQuery = /\bplan|availability|available|tomorrow|schedule|roster\b/i.test(lowerQuery);
    const mentionsCrew = lowerQuery.includes('crew') || lowerQuery.includes('dispatch');
    const isAssetDispatch = lowerQuery.includes('nearest') || /\b(LK|WO)[-\s]?\d{2,5}\b/i.test(query);
    if (mentionsCrew && isPlanQuery && !isAssetDispatch) {
      const districts = ['jefferson', 'northside', 'eastgate', 'riverside'];
      const district = districts.find(d => lowerQuery.includes(d));
      const conditions = [];
      if (district) conditions.push({ field: 'district', operator: '=', value: district.toUpperCase(), type: 'string' });
      return {
        layer: 'crews',
        conditions,
        zoom: true,
        description: `Crew availability${district ? ` in ${district}` : ''} for dispatch planning`,
        confidence: 0.85,
        fallback: true
      };
    }
    // SCADA-style operational keywords first — leak detection / fenceline / dispatch
    const scadaKeywords = ['alert', 'alarm', 'leak', 'leak indication', 'grade', 'fenceline',
      'sensor', 'methane', 'anomaly', 'plume', 'eta', 'crestwood',
      'refinery', 'tank farm', 'fcc', "what's wrong", 'status of', 'most likely source',
      'emergency', 'triggered', 'attribution'];
    if (scadaKeywords.some(k => lowerQuery.includes(k))) {
      const idMatch = query.match(/\b(LK|TM|DM|SL|WO|PR|RT)[-\s]?(\d{2,5})\b/i);
      const sensorMatch = query.match(/\bsensor\s*([A-D])\s*-?\s*(\d{1,2})\b/i);
      const districts = ['jefferson', 'northside', 'eastgate', 'riverside'];
      const district = districts.find(d => lowerQuery.includes(d));
      const filters = {};
      if (idMatch)        filters.asset_id = `${idMatch[1].toUpperCase()}-${idMatch[2].padStart(5, '0')}`;
      else if (sensorMatch) filters.asset_id = `SENSOR-${sensorMatch[1].toUpperCase()}${sensorMatch[2]}`;
      if (district)       filters.county = district.charAt(0).toUpperCase() + district.slice(1) + (district === 'jefferson' ? ' District' : '');
      if (lowerQuery.includes('grade 3') || lowerQuery.includes('emergency') || lowerQuery.includes('critical')) filters.severity = 'critical';
      else if (lowerQuery.includes('grade 2') || lowerQuery.includes('warning')) filters.severity = 'warning';

      let intent = 'list_alerts';
      if (lowerQuery.includes('crew') || lowerQuery.includes('dispatch') || lowerQuery.includes('eta') || lowerQuery.includes('nearest')) intent = 'nearest_crew';
      else if (lowerQuery.includes('source') || lowerQuery.includes('attribution') || lowerQuery.includes('plume')) intent = 'asset_dossier';
      else if (filters.asset_id || lowerQuery.includes("what's wrong") || lowerQuery.includes('status of')) intent = 'asset_dossier';

      return {
        layer: 'scada',
        scope: filters.asset_id ? 'asset' : 'region',
        filters,
        intent,
        description: filters.asset_id
          ? `SCADA dossier for ${filters.asset_id}`
          : `SCADA alerts${filters.county ? ' in ' + filters.county : ''}${filters.severity ? ' (' + filters.severity + ')' : ''}`,
        confidence: 0.6, fallback: true,
      };
    }

    // Pipe replacement / capital planning
    if (lowerQuery.match(/pipe replacement|priority ranking|capital plan|replace.*district|highest risk pipe|pipe risk/i)) {
      const districts = { jefferson: 'JEFFERSON', northside: 'NORTHSIDE', eastgate: 'EASTGATE', riverside: 'RIVERSIDE' };
      const dKey = Object.keys(districts).find(d => lowerQuery.includes(d));
      const conditions = dKey ? [{ field: 'district', operator: '=', value: districts[dKey], type: 'string' }] : [];
      const groupBy = /by district/i.test(lowerQuery) ? 'district' : undefined;
      return { layer: 'pipe_risk', conditions, groupBy, zoom: true, description: dKey ? `Pipe risk ranking for ${districts[dKey]}` : (groupBy ? 'Pipe risk ranking grouped by district' : 'Pipe risk ranking'), confidence: 0.6, fallback: true };
    }

    // Survey routes
    if (lowerQuery.includes('survey') || lowerQuery.includes('route')) {
      return { layer: 'survey_routes', conditions: [], zoom: true, description: 'Showing survey routes', confidence: 0.6, fallback: true };
    }

    // Distribution mains
    if (lowerQuery.includes('distribution') || lowerQuery.includes('main') || lowerQuery.includes('pipe')) {
      return { layer: 'distribution_mains', conditions: [], zoom: true, description: 'Showing distribution mains', confidence: 0.6, fallback: true };
    }

    // Sensitive premises
    if (lowerQuery.includes('school') || lowerQuery.includes('hospital') || lowerQuery.includes('sensitive') || lowerQuery.includes('premise')) {
      // Proximity query: "Grade N within X feet of school/hospital"
      const ftMatch = lowerQuery.match(/within\s+(\d+)\s*(ft|feet)/i);
      const gradeMatch = lowerQuery.match(/grade\s+([123])/i);
      if (ftMatch || gradeMatch) {
        const within_ft = ftMatch ? parseInt(ftMatch[1], 10) : 100;
        const conditions = [];
        if (gradeMatch) conditions.push({ field: 'grade', operator: '=', value: parseInt(gradeMatch[1], 10), type: 'number' });
        const premise_type = lowerQuery.includes('school') && !lowerQuery.includes('hospital') ? 'school'
          : lowerQuery.includes('hospital') && !lowerQuery.includes('school') ? 'hospital'
          : 'both';
        return {
          layer: 'leaks',
          conditions,
          proximity_to: 'sensitive_premises',
          within_ft,
          premise_type,
          zoom: true,
          description: `${conditions.length ? `Grade ${gradeMatch[1]} ` : ''}leaks within ${within_ft} ft of a ${premise_type}`,
          confidence: 0.85,
          fallback: true
        };
      }
      return { layer: 'sensitive_premises', conditions: [], zoom: true, description: 'Showing sensitive premises', confidence: 0.6, fallback: true };
    }

    // Work orders
    if (lowerQuery.includes('work order') || lowerQuery.includes('repair') || lowerQuery.includes('open ticket')) {
      return { layer: 'work_orders_open', conditions: [], zoom: true, description: 'Showing open work orders', confidence: 0.6, fallback: true };
    }

    // Districts
    if (lowerQuery.includes('district')) {
      return { layer: 'districts', conditions: [], zoom: true, description: 'Showing districts', confidence: 0.6, fallback: true };
    }

    // Default fallback for emissions: show leaks
    return { layer: 'leaks', conditions: [], zoom: true, description: 'Showing leak indications', confidence: 0.5, fallback: true };
  }

  // Water tenant: use water layer fallbacks
  if (isWaterTenant) {
    // SCADA / operations intent — must come first because words like "pump" / "alert"
    // can co-occur with layer words.
    const scadaKeywords = ['alert', 'alarm', 'scada', 'telemetry', 'offline', 'outage',
      'pressure', 'troubleshoot', 'fix', 'dispatch', 'crew', 'eta', 'down', 'fault',
      'over capacity', 'overcapacity', 'recovery rate', 'leak', "what's wrong",
      'problem with', 'status of'];
    if (scadaKeywords.some(k => lowerQuery.includes(k))) {
      // Try to extract an asset id like DW-0042, P-23, TF-0275, WS-0100
      const idMatch = query.match(/\b([A-Z]{1,3})[-\s]?(\d{2,5})\b/);
      const counties = ['reeves', 'midland', 'eddy', 'ector', 'lea', 'ward', 'loving',
        'martin', 'pecos', 'winkler'];
      const county = counties.find(c => lowerQuery.includes(c));
      const filters = {};
      if (idMatch) filters.asset_id = `${idMatch[1]}-${idMatch[2].padStart(4, '0')}`;
      if (county) filters.county = county.charAt(0).toUpperCase() + county.slice(1);
      if (lowerQuery.includes('critical')) filters.severity = 'critical';

      let intent = 'list_alerts';
      if (lowerQuery.includes('crew') || lowerQuery.includes('dispatch') ||
          lowerQuery.includes('fix') || lowerQuery.includes('nearest') ||
          lowerQuery.includes('eta')) intent = 'nearest_crew';
      else if (lowerQuery.includes('impact') || lowerQuery.includes('capacity offline')) intent = 'impact';
      else if (filters.asset_id || lowerQuery.includes("what's wrong") ||
               lowerQuery.includes('status of')) intent = 'asset_dossier';

      return {
        layer: 'scada',
        scope: filters.asset_id ? 'asset' : 'region',
        filters,
        intent,
        description: filters.asset_id
          ? `SCADA dossier for ${filters.asset_id}`
          : `SCADA alerts${filters.county ? ' in ' + filters.county : ''}${filters.severity ? ' (' + filters.severity + ')' : ''}`,
        confidence: 0.6, fallback: true,
      };
    }

    if (lowerQuery.includes('source') || lowerQuery.includes('water source')) {
      return { layer: 'sources', conditions: [], zoom: true, description: 'Showing water sources', confidence: 0.6, fallback: true };
    }
    if (lowerQuery.includes('disposal') || lowerQuery.includes('inject')) {
      return { layer: 'disposal', conditions: [], zoom: true, description: 'Showing disposal wells', confidence: 0.6, fallback: true };
    }
    if (lowerQuery.includes('pipeline') || lowerQuery.includes('transfer')) {
      return { layer: 'pipelines', conditions: [], zoom: true, description: 'Showing transfer pipelines', confidence: 0.6, fallback: true };
    }
    if (lowerQuery.includes('treatment') || lowerQuery.includes('facility') || lowerQuery.includes('facilities')) {
      return { layer: 'treatment', conditions: [], zoom: true, description: 'Showing treatment facilities', confidence: 0.6, fallback: true };
    }
    // Default water layer
    return { layer: 'sources', conditions: [], zoom: true, description: 'Showing water sources', confidence: 0.5, fallback: true };
  }

  // Pattern: tract search
  const tractPattern = /(?:show|find).*tract[s]?\s+(?:in\s+)?([a-z]+)\s*county/i;
  const tractMatch = query.match(tractPattern);
  if (tractMatch) {
    return {
      layer: 'tracts',
      conditions: [{
        field: 'CountyName',
        operator: '=',
        value: tractMatch[1].toUpperCase(),
        type: 'string'
      }],
      zoom: true,
      description: `Showing tracts in ${tractMatch[1]} County`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Pattern: operator filter for wells
  if (lowerQuery.includes('operated by') || lowerQuery.includes('operator')) {
    const operatorPattern = /operated?\s+by\s+([a-z0-9\s,&]+)/i;
    const operatorMatch = query.match(operatorPattern);

    if (operatorMatch) {
      const operatorName = operatorMatch[1].trim();
      return {
        layer: 'wells',
        conditions: [{
          field: 'ENVOperator',
          operator: 'LIKE',
          value: `%${operatorName}%`,
          type: 'string'
        }],
        zoom: false,
        description: `Filtering wells by operator: ${operatorName}`,
        confidence: 0.7,
        fallback: true
      };
    }
  }

  // Pattern: well status filter
  if (lowerQuery.includes('producing') || lowerQuery.includes('drilling') || lowerQuery.includes('duc')) {
    let status = 'PRODUCING';
    if (lowerQuery.includes('drilling')) status = 'DRILLING';
    if (lowerQuery.includes('duc') || lowerQuery.includes('uncompleted')) status = 'DUC';
    if (lowerQuery.includes('permitted')) status = 'PERMITTED';
    if (lowerQuery.includes('shut-in') || lowerQuery.includes('shut in')) status = 'SHUT-IN';

    return {
      layer: 'wells',
      conditions: [{
        field: 'ENVWellStatus',
        operator: '=',
        value: status,
        type: 'string'
      }],
      zoom: false,
      description: `Filtering to ${status} wells`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Pattern: formation filter
  const formations = ['wolfcamp', 'bone spring', 'avalon', 'delaware', 'spraberry'];
  for (const formation of formations) {
    if (lowerQuery.includes(formation)) {
      return {
        layer: 'wells',
        conditions: [{
          field: 'ENVInterval',
          operator: 'LIKE',
          value: `%${formation.toUpperCase()}%`,
          type: 'string'
        }],
        zoom: false,
        description: `Showing ${formation} wells`,
        confidence: 0.7,
        fallback: true
      };
    }
  }

  // Pattern: county filter for wells
  const countyPattern = /(?:wells?|in)\s+([a-z]+)\s*county/i;
  const countyMatch = query.match(countyPattern);
  if (countyMatch) {
    return {
      layer: 'wells',
      conditions: [{
        field: 'County',
        operator: '=',
        value: countyMatch[1].toUpperCase(),
        type: 'string'
      }],
      zoom: true,
      description: `Showing wells in ${countyMatch[1]} County`,
      confidence: 0.7,
      fallback: true
    };
  }

  // Default: return tenant-aware error
  if (isEmissionsTenant) {
    throw new Error('Could not understand query. Try: "Show critical leaks in Jefferson", "Pipe replacement priority for Northside", or "Active anomalies at Crestwood Refinery"');
  }
  if (isWaterTenant) {
    throw new Error('Could not understand query. Try: "Show critical disposal wells in Reeves" or "Treatment facilities in Midland"');
  }
  throw new Error('Could not understand query. Try: "Show wells operated by Diamondback" or "Find tracts in Howard County"');
}

/**
 * Suggest example queries to help users
 */
export function getExampleQueries() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Select Water Solutions tenant
  if (hostname.includes('nexus-water') || hostname.includes('water')) {
    return [
      {
        category: '💧 Water Sources',
        queries: [
          'Show all water sources',
          'Find water sources in Reeves County',
          'Show high-volume water sources',
          'Find active sourcing operations'
        ]
      },
      {
        category: '🏭 Disposal Wells',
        queries: [
          'Show disposal wells in Midland',
          'Find high-utilization disposal wells',
          'Show disposal capacity by county',
          'Find disposal wells by operator'
        ]
      },
      {
        category: '🔧 Treatment Facilities',
        queries: [
          'Show all treatment facilities',
          'Find facilities with high recycling rates',
          'Show treatment capacity in Permian',
          'Find facilities using advanced technology'
        ]
      },
      {
        category: '🚿 Transfer Pipelines',
        queries: [
          'Show water transfer pipelines',
          'Find pipelines in Reeves County',
          'Show high-capacity pipeline routes',
          'Find active transfer operations'
        ]
      },
      {
        category: '⚡ SCADA Operations',
        queries: [
          'Show critical alerts in Reeves County',
          'What\'s wrong with disposal well DW-0042',
          'Who is the nearest crew to fix P-23',
          'List all critical disposal alerts'
        ]
      }
    ];
  }

  // Picarro POC tenant
  if (hostname.includes('emissions')) {
    return [
      {
        category: '🔥 Leak Detection',
        queries: [
          'Show Grade 2+ leaks in Jefferson District from Q3 2026',
          'Which Grade 2 leaks are within 100 feet of a school or hospital',
          'Have any of these leak locations shown up in previous surveys',
          'List all Grade 3 emergency leaks',
        ],
      },
      {
        category: '🛠 Capital Planning',
        queries: [
          'Build a pipe replacement priority ranking for Northside',
          'Show pipe risk scores by district',
          'Which pipe segments have leak indications in 3+ surveys',
          'Show distribution mains older than 1965 by district',
        ],
      },
      {
        category: '👷 Crew Dispatch',
        queries: [
          'Build a dispatch list for tomorrow\'s repair crews',
          'Who is the nearest crew to fix LK-00012',
          'Show all open Emergency work orders',
          'Crew assignments for Northside today',
        ],
      },
      {
        category: '🏭 Fenceline Monitoring',
        queries: [
          'Sensor B3 flagged elevated methane — what is the most likely source',
          'Compare readings from all 12 sensors in the eastern array',
          'Show active anomaly events at Crestwood Refinery',
          'Draft incident notification for the Tank Farm event',
        ],
      },
      {
        category: '📋 Compliance',
        queries: [
          'Generate Q3 compliance summary for Jefferson District',
          'Survey coverage status by district',
          'All open work orders past their target date',
          'Total repair cost by district last quarter',
        ],
      },
    ];
  }

  // Default TPL queries
  return [
    {
      category: 'Land & Royalty Interest',
      queries: [
        'Show tracts in Reeves County',
        'Find fee tracts in Howard County',
        'Show tracts with NRI over 0.01',
        'Find tracts with ORRI over 0.02'
      ]
    },
    {
      category: 'Acreage Analysis',
      queries: [
        'Show tracts over 200 acres',
        'Find tracts with NRA over 20',
        'Show tracts over 500 acres in Culberson',
        'Find tracts in Midland County'
      ]
    },
    {
      category: 'Deal & Asset Tracking',
      queries: [
        'Show SM ORRI Deal 1 tracts',
        'Find Tumbleweed Deal assets',
        'Show Allegro Deal tracts',
        'Find DE ORRI Deal 1 tracts'
      ]
    },
    {
      category: 'Geographic Focus',
      queries: [
        'Show all tracts in Culberson County',
        'Find tracts in Glasscock County',
        'Show tracts in Martin County',
        'Find tracts in Loving County'
      ]
    },
    {
      category: 'Survey & Block',
      queries: [
        'Find T&P RR CO survey tracts',
        'Show tracts in Section 10',
        'Find L&SV RR CO surveys',
        'Show tracts in Section 1'
      ]
    },
    {
      category: 'Advanced Queries',
      queries: [
        'Fee tracts over 300 acres in Reeves',
        'Tracts with NRI over 0.02 in Howard',
        'Large tracts in Culberson County',
        'Show Upton County tracts over 100 acres'
      ]
    },
    {
      category: '🌟 Multi-Layer (NEW!)',
      queries: [
        'Show all data in Reeves County',
        'Show tracts and wells in Howard County',
        'Display all layers for Culberson',
        'Show everything in Midland County'
      ]
    }
  ];
}

// Export conversation service for use in other components
export { conversationService };

export default {
  translateQuery,
  getExampleQueries
};
